import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '../types';
import { Colors, Fonts, Radii, Spacing, Typography, StatusTints, StatusTintKey } from '../constants';
import { useUnreadCountContext, useUser } from '../contexts';

// Square app icon (mobile header)
const IconLogo = require('../../Logos/clubswap_icon.png');

export type StatusBadgeVariant = StatusTintKey;

export interface StatusBadge {
  label: string;
  variant: StatusBadgeVariant;
}

interface AppHeaderProps {
  navigation: {
    navigate: (screen: keyof RootStackParamList, params?: any) => void;
    goBack?: () => void;
  };
  /** Kept for call-site compatibility; navigation now lives in the bottom TabBar */
  currentScreen?: 'Home' | 'Browse' | 'Profile' | 'Clubs' | 'ClubDetail' | 'ItemDetails' | 'ItemForm' | 'ClubLeaderboard' | 'ConversationsList' | 'Chat';
  title: string;
  /** Detail variant: back arrow + Lora 20 title instead of app icon + Lora 24 title */
  showBackButton?: boolean;
  /** @deprecated the header no longer holds a tab row; navigation is in TabBar */
  hideMenu?: boolean;
  /** Tinted status tag on the right ("Reserved") */
  statusBadge?: StatusBadge;
  /** Hide the bell on the main variant */
  hideBell?: boolean;
}

/**
 * Mobile header (design v2).
 *
 * Main variant: white with a 1px hairline bottom border, 32px app icon +
 * Lora 24 Bosgroen title + bell. Detail variant (`showBackButton`): back
 * arrow, Lora 20 title, optional status tag. Tabs live in the bottom
 * `TabBar`, not here.
 */
export const AppHeader: React.FC<AppHeaderProps> = ({
  navigation,
  title,
  showBackButton = false,
  statusBadge,
  hideBell = false,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { unreadCount } = useUnreadCountContext();
  const insets = useSafeAreaInsets();

  const safeTop = Platform.OS === 'web' ? 0 : insets.top;
  const tint = statusBadge ? StatusTints[statusBadge.variant] ?? StatusTints.neutral : null;
  const showBell = !showBackButton && !hideBell && !!user;

  return (
    <View style={[styles.container, { paddingTop: Spacing.md + safeTop }]}>
      <View style={styles.row}>
        {showBackButton && navigation.goBack ? (
          <TouchableOpacity
            onPress={navigation.goBack}
            style={styles.iconButton}
            accessibilityRole="button"
            accessibilityLabel={t('common.buttons.back')}
            testID="header-back"
          >
            <Ionicons name="arrow-back" size={24} color={Colors.textDark} />
          </TouchableOpacity>
        ) : (
          <Image source={IconLogo} style={styles.logo} resizeMode="contain" />
        )}

        <Text
          style={[styles.title, showBackButton ? styles.titleDetail : styles.titleMain]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {title}
        </Text>

        {statusBadge && tint ? (
          <View style={[styles.statusTag, { backgroundColor: tint.bg }]}>
            <Text style={[styles.statusTagText, { color: tint.fg }]}>{statusBadge.label}</Text>
          </View>
        ) : null}

        {showBell ? (
          <TouchableOpacity
            onPress={() => navigation.navigate('ConversationsList')}
            style={styles.iconButton}
            accessibilityRole="button"
            accessibilityLabel={t('navigation.conversations')}
          >
            <Ionicons name="notifications-outline" size={22} color={Colors.textMedium} />
            {unreadCount > 0 ? <View style={styles.bellDot} /> : null}
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    paddingHorizontal: Spacing.base,
    paddingBottom: Spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
    zIndex: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minHeight: 36,
  },
  iconButton: {
    padding: Spacing.xs,
    margin: -Spacing.xs,
  },
  logo: {
    width: 32,
    height: 32,
    borderRadius: Radii.thumb,
  },
  title: {
    flex: 1,
    flexShrink: 1,
  },
  titleMain: {
    ...Typography.pageTitle,
    color: Colors.textDark,
  },
  titleDetail: {
    ...Typography.detailTitle,
    color: Colors.textDark,
  },
  statusTag: {
    paddingHorizontal: 10,
    paddingVertical: Spacing.xs,
    borderRadius: Radii.tag,
    flexShrink: 0,
  },
  statusTagText: {
    fontFamily: Fonts.semiBold,
    fontSize: 12,
    lineHeight: 14,
  },
  bellDot: {
    position: 'absolute',
    top: 4,
    right: 5,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.primary,
    borderWidth: 1.5,
    borderColor: Colors.white,
  },
});
